import React, { useRef, useState } from 'react';
import { carteraApi } from './api';
import { money } from './helpers';
import ModalPortal from './ModalPortal';
import {
  FORMAS, Opciones, ResultadoBuroCampos, RESULTADO_INICIAL,
  armarResultadoBuro, hoyLocal, parseMonto, validarResultadoBuro,
} from './CambiarResultadoBuroModal';

const ESTATUS_TXT = { vigente: 'Al corriente', vencido: 'Vencido', deteriorado: 'Deteriorado', liquidado: 'Liquidado', castigado: 'Castigado' };

/**
 * Liquidar un crédito por recuperación de auto: NO registra ningún pago; el
 * back condona rentas/mora pendientes y deja el crédito en 'liquidado'.
 * Flujo: formulario → vista previa (no escribe) → resultado para Buró
 * (titular y aval) → confirmar → éxito.
 */
export const RecuperarAutoModal = ({ credito, onClose, onDone }) => {
  const [fecha, setFecha] = useState(hoyLocal());
  const [valorAuto, setValorAuto] = useState('');
  const [motivo, setMotivo] = useState('');
  const [notas, setNotas] = useState('');
  const [forma, setForma] = useState('entrega');
  // Elección de resultado para Buró (titular/aval/monto). No invalida la vista
  // previa: no cambia ni la deuda ni el faltante.
  const [resultado, setResultado] = useState({ ...RESULTADO_INICIAL });
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);
  // Cuenta las ediciones del formulario: una vista previa que salió antes de la
  // última edición ya no corresponde a lo que hay en pantalla y se descarta.
  const ediciones = useRef(0);

  const c = credito || {};
  const valorNum = parseMonto(valorAuto);

  const tieneAval = !!preview?.tiene_aval && (preview?.aval_con_resultado ?? c.aval_con_resultado) !== false;
  const faltante = preview?.faltante != null ? Number(preview.faltante) : null;

  const armarBody = () => ({
    fecha_entrega: fecha,
    valor_auto: valorNum,
    motivo: motivo.trim(),
    notas: notas.trim() || null,
    forma,
  });

  const validar = () => {
    if (!fecha) return 'Indica la fecha de entrega del auto.';
    if (fecha > hoyLocal()) return 'La fecha de entrega no puede ser futura.';
    if (!motivo.trim()) return 'Escribe el motivo.';
    return null;
  };

  async function verVistaPrevia() {
    const msg = validar();
    if (msg) { setError(msg); return; }
    const mias = ediciones.current;
    setPreviewLoading(true);
    setError(null);
    setPreview(null);
    try {
      const res = await carteraApi.recuperacionPreview(c.id, armarBody());
      if (mias !== ediciones.current) return; // se editó el formulario mientras calculaba
      setPreview(res?.data?.data ?? res?.data ?? null);
    } catch (e) {
      if (mias !== ediciones.current) return;
      setError(e?.response?.data?.message || 'No se pudo calcular la vista previa.');
    } finally {
      setPreviewLoading(false);
    }
  }

  async function confirmar() {
    const msg = validar() || validarResultadoBuro(resultado, tieneAval, faltante);
    if (msg) { setError(msg); return; }
    // Si el auto cubre la deuda no hay nada que elegir: no se manda resultado_buro.
    const body = armarBody();
    const rb = armarResultadoBuro(resultado, tieneAval, faltante);
    if (rb) body.resultado_buro = rb;
    setSubmitting(true);
    setError(null);
    try {
      await carteraApi.recuperar(c.id, body);
      setOk(true);
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo liquidar el crédito por recuperación.');
    } finally {
      setSubmitting(false);
    }
  }

  function cerrarOk() {
    if (onDone) onDone();
    onClose();
  }

  const invalidar = () => { ediciones.current += 1; if (preview) setPreview(null); };
  // Fecha y valor del auto mueven la deuda/faltante: el monto que se había
  // escrito a mano ya no aplica y vuelve a proponerse el faltante nuevo.
  const reproponerMonto = () => setResultado((r) => (r.monto == null ? r : { ...r, monto: null }));
  const dif = preview?.valor_auto != null && preview?.diferencia != null ? Number(preview.diferencia) : null;
  const antes = preview?.antes || {};
  const despues = preview?.despues || {};

  return (
    <ModalPortal>
      <div className="cart-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) (ok ? cerrarOk() : onClose()); }}>
        <div className="cart-modal">
          <div className="cm-head">
            <h3>Liquidar por recuperación de auto</h3>
            <button className="cm-close" onClick={() => (ok ? cerrarOk() : onClose())}><i className="pi pi-times" /></button>
          </div>
          <div className="cm-credit">
            <b>{c.cliente || '—'}</b> · {c.clave_contrato} · saldo <b>{money(c.saldo_insoluto)}</b>
          </div>

          {ok ? (
            <>
              <div className="cm-result">
                <div className="cr-folio">Recuperación registrada</div>
                <div className="cr-row"><span>Capital cubierto</span><b>{money(preview?.capital_cubierto)}</b></div>
                <div className="cr-row"><span>Estatus del crédito</span><b>Liquidado</b></div>
              </div>
              <div className="cm-note" style={{ marginTop: 8 }}>No se registró ningún pago.</div>
              <div className="cm-actions">
                <button className="btn btn-green btn-sm" onClick={cerrarOk}><i className="pi pi-check" />Listo</button>
              </div>
            </>
          ) : (
            <>
              {error && <div className="cm-error">{error}</div>}

              <div className="cm-row">
                <div className="cm-field">
                  <label>Fecha de entrega del auto</label>
                  <input className="cm-input" type="date" value={fecha} max={hoyLocal()}
                    onChange={(e) => { setFecha(e.target.value); invalidar(); reproponerMonto(); }} />
                </div>
                <div className="cm-field">
                  <label>Valor del auto (opcional)</label>
                  <input className="cm-input" type="text" inputMode="decimal" placeholder="0.00"
                    value={valorAuto}
                    onChange={(e) => { setValorAuto(e.target.value); invalidar(); reproponerMonto(); }} />
                  {valorNum != null && valorNum > 0 && <div className="cm-note">= {money(valorNum)}</div>}
                </div>
              </div>
              <div className="cm-note" style={{ marginTop: -4, marginBottom: 10 }}>
                Sirve para ver la pérdida o ganancia contra el capital pendiente
              </div>

              <div className="cm-field">
                <label>Motivo</label>
                <input className="cm-input" type="text" value={motivo} maxLength={250}
                  onChange={(e) => { setMotivo(e.target.value); invalidar(); }}
                  placeholder="Ej. Cliente entregó la unidad por impago" />
              </div>
              <div className="cm-field">
                <label>Notas (opcional)</label>
                <textarea className="cm-input" value={notas} onChange={(e) => { setNotas(e.target.value); invalidar(); }} placeholder="Observaciones…" />
              </div>

              <div className="cm-field">
                <label>¿Cómo se recuperó?</label>
                <Opciones nombre="forma-recuperacion" etiqueta="Cómo se recuperó" valor={forma} opciones={FORMAS}
                  onChange={(v) => { setForma(v); invalidar(); }} />
              </div>

              <div className="cm-note">
                No se registra ningún pago. El seguro, las placas y la tenencia no se cancelan.
              </div>

              {preview && (
                <div className="cm-result" style={{ marginTop: 12 }}>
                  <div className="cr-row"><span>Capital cubierto</span><b>{money(preview.capital_cubierto)}</b></div>
                  <div className="cr-row">
                    <span>Rentas que se perdonan ({Number(preview.rentas_condonadas?.count || 0)})</span>
                    <b>{money(preview.rentas_condonadas?.monto)}</b>
                  </div>
                  <div className="cr-row"><span>Mora que se perdona</span><b>{money(preview.mora_condonada)}</b></div>
                  <div className="cr-row"><span>Saldo</span><b>{money(antes.saldo)} → {money(despues.saldo)}</b></div>
                  <div className="cr-row">
                    <span>Estatus</span>
                    <b>{ESTATUS_TXT[antes.estatus] || antes.estatus || '—'} → Liquidado</b>
                  </div>
                  {dif != null && (
                    <div className="cr-row">
                      <span>Valor del auto {money(preview.valor_auto)}</span>
                      {dif < 0
                        ? <b style={{ color: '#b91c1c' }}>Pérdida {money(Math.abs(dif))}</b>
                        : <b style={{ color: '#06794b' }}>Ganancia {money(dif)}</b>}
                    </div>
                  )}
                </div>
              )}

              {preview && (
                <ResultadoBuroCampos
                  deuda={preview.deuda != null ? Number(preview.deuda) : null}
                  valorAuto={preview.valor_auto != null ? Number(preview.valor_auto) : null}
                  faltante={faltante}
                  tieneAval={tieneAval}
                  avalNombre={preview.aval_nombre}
                  value={resultado}
                  onChange={setResultado}
                  disabled={submitting}
                />
              )}

              <div className="cm-actions">
                <button className="btn btn-sm" onClick={onClose} disabled={submitting}>Cancelar</button>
                {!preview ? (
                  <button className="btn btn-green btn-sm" onClick={verVistaPrevia} disabled={previewLoading}>
                    <i className="pi pi-eye" />{previewLoading ? 'Calculando…' : 'Ver vista previa'}
                  </button>
                ) : (
                  <button className="btn btn-green btn-sm" onClick={confirmar} disabled={submitting}>
                    <i className="pi pi-check" />{submitting ? 'Liquidando…' : 'Confirmar'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

export default RecuperarAutoModal;
